import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, HStack, Input, Select, Table, Tbody, Td, Th, Thead, Tr,
  Text, Badge, IconButton, Spinner, useToast
} from '@chakra-ui/react';
import { DownloadIcon, ChevronLeftIcon, ChevronRightIcon, RepeatIcon } from '@chakra-ui/icons';
import TableSkeleton from '../../components/TableSkeleton';

type Webhook = {
  Id: number;
  EventType: string;
  EventDate?: string | null;
  TransactionId?: string | null;
  Status?: string | null;
  Amount?: string | number | null;
  Currency?: string | null;
  Description?: string | null;
  CustomerName?: string | null;
  CustomerEmail?: string | null;
  CardBrand?: string | null;
  CardNumber?: string | null;
  Authorization?: string | null;
  RawJson: string;
  CreatedAt: string;
};

type ApiResponse = {
  data: Webhook[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

const BASE_URL = 'https://totolac-backend.togui.com.mx/api/';

function buildQuery(params: Record<string, any>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    search.set(k, String(v));
  });
  return search.toString();
}

function joinUrl(path: string) {
  return `${BASE_URL.replace(/\/+$/,'')}/${path.replace(/^\/+/,'')}`;
}

function toCSV(rows: Webhook[]) {
  const headers = [
    'Id','EventType','EventDate','TransactionId','Status','Amount','Currency',
    'Description','CustomerName','CustomerEmail','CardBrand','CardNumber','Authorization','CreatedAt'
  ];
  const esc = (val: any) => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.Id, r.EventType, r.EventDate ?? '', r.TransactionId ?? '', r.Status ?? '',
      r.Amount ?? '', r.Currency ?? '', r.Description ?? '', r.CustomerName ?? '',
      r.CustomerEmail ?? '', r.CardBrand ?? '', r.CardNumber ?? '', r.Authorization ?? '',
      r.CreatedAt
    ].map(esc).join(','))
  ];
  return lines.join('\n');
}

export default function TablaOpenpayWebhooks() {
  const toast = useToast();

  const [q, setQ] = useState('');
  const [type, setType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [txid, setTxid] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<'CreatedAt'|'EventDate'|'Amount'|'Id'|'Status'|'EventType'>('CreatedAt');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Webhook[]>([]);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = buildQuery({
        q: qDebounced || undefined,
        type: type || undefined,
        status: status || undefined,
        email: email || undefined,
        txid: txid || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        page,
        pageSize,
        sort: `${sortField}:${sortDir}`
      });
      const url = `${joinUrl('openpay-webhooks')}?${qs}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ApiResponse = await res.json();
      setRows(json.data);
      setTotal(json.pagination.total);
    } catch (err: any) {
      toast({ status: 'error', title: 'Error cargando datos', description: String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [qDebounced, type, status, email, txid, from, to, page, pageSize, sortField, sortDir]);

  const resetFilters = () => {
    setQ(''); setType(''); setStatus(''); setEmail(''); setTxid(''); setFrom(''); setTo(''); setPage(1);
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const exportFiltered = async (format: 'csv'|'json') => {
    try {
      setLoading(true);
      const all: Webhook[] = [];
      let current = 1;
      const hardLimitPages = 500;

      while (current <= hardLimitPages) {
        const qs = buildQuery({
          q: qDebounced || undefined,
          type: type || undefined,
          status: status || undefined,
          email: email || undefined,
          txid: txid || undefined,
          from: from ? new Date(from).toISOString() : undefined,
          to: to ? new Date(to).toISOString() : undefined,
          page: current,
          pageSize: 1000,
          sort: `${sortField}:${sortDir}`
        });
        const url = `${joinUrl('openpay-webhooks')}?${qs}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: ApiResponse = await res.json();
        all.push(...json.data);
        if (current >= json.pagination.totalPages) break;
        current += 1;
      }

      let blob: Blob;
      let filename: string;
      if (format === 'csv') {
        blob = new Blob([toCSV(all)], { type: 'text/csv;charset=utf-8' });
        filename = 'openpay-webhooks.csv';
      } else {
        blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json;charset=utf-8' });
        filename = 'openpay-webhooks.json';
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast({ status: 'success', title: 'Exportación lista', description: `Se exportaron ${all.length} registros.` });
    } catch (err: any) {
      toast({ status: 'error', title: 'Error exportando', description: String(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4} pt={79} width="100%">
      <Flex justify="space-between" align="center" mb={6} gap={3} wrap="wrap">
        <HStack>
          <Button 
            leftIcon={<DownloadIcon />} 
            size="sm" 
            bg="#bb8c52" 
            color="white" 
            _hover={{ bg: "#a67a47" }}
            onClick={() => exportFiltered('csv')} 
            isLoading={loading}
          >
            Exportar CSV
          </Button>
          <Button 
            leftIcon={<DownloadIcon />} 
            size="sm" 
            variant="outline" 
            borderColor="#bb8c52" 
            color="#bb8c52" 
            _hover={{ bg: "#bb8c52", color: "white" }}
            onClick={() => exportFiltered('json')} 
            isLoading={loading}
          >
            Exportar JSON
          </Button>
          <IconButton 
            aria-label="Refrescar" 
            icon={<RepeatIcon />} 
            size="sm" 
            variant="outline"
            borderColor="#681a2d" 
            color="#681a2d" 
            _hover={{ bg: "#681a2d", color: "white" }}
            onClick={fetchData} 
            isLoading={loading}
          />
        </HStack>
      </Flex>

      <Box 
        mb={6} 
        p={4} 
        bg="white" 
        borderRadius="lg" 
        boxShadow="sm" 
        border="1px solid" 
        borderColor="gray.200"
      >
        <Flex gap={3} wrap="wrap">
          <Input 
            placeholder="Buscar en Description…" 
            value={q} 
            onChange={(e) => { setQ(e.target.value); setPage(1); }} 
            maxW="280px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
          <Select 
            placeholder="Tipo de evento" 
            value={type} 
            onChange={(e)=>{ setType(e.target.value); setPage(1); }} 
            maxW="220px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          >
            <option value="charge.succeeded">charge.succeeded</option>
            <option value="charge.failed">charge.failed</option>
            <option value="spei.received">spei.received</option>
            <option value="transfer.succeeded">transfer.succeeded</option>
            <option value="charge.3d_secure_authenticated">charge.3d_secure_authenticated</option>
          </Select>
          <Select 
            placeholder="Estatus" 
            value={status} 
            onChange={(e)=>{ setStatus(e.target.value); setPage(1); }} 
            maxW="180px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          >
            <option value="completed">completed</option>
            <option value="failed">failed</option>
            <option value="in_progress">in_progress</option>
          </Select>
          <Input 
            placeholder="Email" 
            value={email} 
            onChange={(e)=>{ setEmail(e.target.value); setPage(1); }} 
            maxW="240px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
          <Input 
            placeholder="Transaction ID" 
            value={txid} 
            onChange={(e)=>{ setTxid(e.target.value); setPage(1); }} 
            maxW="300px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
          <Input 
            type="datetime-local" 
            value={from} 
            onChange={(e)=>{ setFrom(e.target.value); setPage(1); }} 
            maxW="220px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
          <Input 
            type="datetime-local" 
            value={to} 
            onChange={(e)=>{ setTo(e.target.value); setPage(1); }} 
            maxW="220px"
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          />
          <Button 
            variant="outline" 
            borderColor="#681a2d" 
            color="#681a2d" 
            _hover={{ bg: "#681a2d", color: "white" }}
            onClick={resetFilters}
          >
            Limpiar filtros
          </Button>
        </Flex>
      </Box>

      <Box 
        bg="white" 
        borderRadius="lg" 
        boxShadow="sm" 
        border="1px solid" 
        borderColor="gray.200"
        overflow="hidden"
        mb={6}
      >
        <Box overflowX="auto">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th 
                  cursor="pointer" 
                  onClick={()=>toggleSort('CreatedAt')}
                  color="#681a2d" 
                  fontWeight="bold" 
                  fontSize="sm"
                  _hover={{ bg: "#bb8c5210" }}
                >
                  Creado {sortField==='CreatedAt' ? (sortDir==='asc'?'▲':'▼') : ''}
                </Th>
                <Th 
                  cursor="pointer" 
                  onClick={()=>toggleSort('EventType')}
                  color="#681a2d" 
                  fontWeight="bold" 
                  fontSize="sm"
                  _hover={{ bg: "#bb8c5210" }}
                >
                  Tipo {sortField==='EventType' ? (sortDir==='asc'?'▲':'▼') : ''}
                </Th>
                <Th 
                  cursor="pointer" 
                  onClick={()=>toggleSort('Status')}
                  color="#681a2d" 
                  fontWeight="bold" 
                  fontSize="sm"
                  _hover={{ bg: "#bb8c5210" }}
                >
                  Estatus {sortField==='Status' ? (sortDir==='asc'?'▲':'▼') : ''}
                </Th>
                <Th 
                  cursor="pointer" 
                  onClick={()=>toggleSort('Amount')}
                  color="#681a2d" 
                  fontWeight="bold" 
                  fontSize="sm"
                  _hover={{ bg: "#bb8c5210" }}
                >
                  Monto {sortField==='Amount' ? (sortDir==='asc'?'▲':'▼') : ''}
                </Th>
                <Th w="30%" color="#681a2d" fontWeight="bold" fontSize="sm">Description</Th>
                <Th color="#681a2d" fontWeight="bold" fontSize="sm">TxID</Th>
                <Th color="#681a2d" fontWeight="bold" fontSize="sm">Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading && rows.length === 0 ? (
                <>
                  {Array.from({ length: pageSize }).map((_, index) => (
                    <Tr key={index}>
                      {Array.from({ length: 7 }).map((_, colIndex) => (
                        <Td key={colIndex}>
                          <Box height="16px" bg="gray.200" borderRadius="md" />
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </>
              ) : rows.length === 0 ? (
                <Tr><Td colSpan={7}><Text>No hay datos</Text></Td></Tr>
              ) : (
                rows.map(r => (
                  <Tr 
                    key={r.Id}
                    _hover={{ 
                      bg: "#bb8c5210"
                    }}
                  >
                    <Td><Text fontSize="sm" color="gray.700">{new Date(r.CreatedAt).toLocaleString()}</Text></Td>
                    <Td>
                      <Badge 
                        bg="#bb8c52" 
                        color="white" 
                        fontSize="xs" 
                        borderRadius="full"
                      >
                        {r.EventType}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge 
                        bg={
                          r.Status === 'completed' ? '#28a745' :
                          r.Status === 'failed' ? '#dc3545' :
                          '#6c757d'
                        }
                        color="white"
                        fontSize="xs" 
                        borderRadius="full"
                      >
                        {r.Status ?? '-'}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <Text fontWeight="semibold" color="#681a2d">
                        {r.Amount ?? ''}
                      </Text>
                    </Td>
                    <Td><Text noOfLines={2} fontSize="sm">{r.Description ?? ''}</Text></Td>
                    <Td>
                      <Text 
                        noOfLines={1} 
                        title={r.TransactionId ?? ''} 
                        fontSize="xs" 
                        fontFamily="mono"
                        color="gray.600"
                      >
                        {r.TransactionId ?? ''}
                      </Text>
                    </Td>
                    <Td><Text noOfLines={1} fontSize="sm">{r.CustomerEmail ?? ''}</Text></Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
        <HStack>
          <IconButton
            aria-label="Anterior"
            icon={<ChevronLeftIcon/>}
            onClick={()=> setPage(p => Math.max(1, p-1))}
            isDisabled={page <= 1 || loading}
            size="sm"
            variant="outline"
            borderColor="#bb8c52"
            color="#bb8c52"
            _hover={{ bg: "#bb8c52", color: "white" }}
          />
          <Text fontSize="sm" color="#681a2d" fontWeight="medium">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            aria-label="Siguiente"
            icon={<ChevronRightIcon/>}
            onClick={()=> setPage(p => Math.min(totalPages, p+1))}
            isDisabled={page >= totalPages || loading}
            size="sm"
            variant="outline"
            borderColor="#bb8c52"
            color="#bb8c52"
            _hover={{ bg: "#bb8c52", color: "white" }}
          />
        </HStack>
        <HStack>
          <Text fontSize="sm" color="#681a2d">Filas:</Text>
          <Select
            size="sm"
            w="80px"
            value={pageSize}
            onChange={(e)=>{ setPageSize(parseInt(e.target.value,10)); setPage(1); }}
            _focus={{ borderColor: "#bb8c52", boxShadow: "0 0 0 1px #bb8c52" }}
          >
            {[10,20,50,100,200].map(n => <option key={n} value={n}>{n}</option>)}
          </Select>
        </HStack>
      </Flex>
    </Box>
  );
}